/**
 * Notes' recipes as data (Mac recipes R3), from the research (apple/notes.md). Each passes the program
 * lint and ships held until the Notes drill lifts it. What is here types into a field only after the
 * focus proved it is that field, and never presses a key after the person's words. Every recipe that
 * names a note by its title waits for the notes.index resolver and its verbs, on the R3 review lists.
 */
import { opening, recipeApp, type OsRecipe } from '../os-recipes.js';

const NOTES = recipeApp('com.apple.Notes');

/** A reversible chord in the running app. */
const chord = (id: string, title: string, description: string, key: string, cues: readonly string[], reply: string): OsRecipe => ({
  id, app: NOTES, status: 'held', title, description, params: {}, steps: [{ op: 'activate' }, { op: 'key', chord: key }], tier: 'reversible', cues, reply,
});

/** A reversible menu press in the running app (R3c); `pair` is a Show/Hide title's opposite, so a press
 *  that finds the other title says it is likely already so. The reply names the item it matched. */
const menu = (id: string, title: string, description: string, path: readonly [string, string, ...string[]], cues: readonly string[], reply: string, pair?: string): OsRecipe => ({
  id, app: NOTES, status: 'held', title, description, params: {},
  steps: [{ op: 'activate' }, { op: 'menu', path, ...(pair ? { pair: [path.at(-1)!, pair] as const } : {}) }],
  tier: 'reversible', verify: 'echo', cues, reply,
});

/** The words every sort shares. */
const SORT: readonly string[] = ['sort', 'order', 'sort notes', 'sort my notes'];

export const RECIPES: readonly OsRecipe[] = [
  // ── New notes ─────────────────────────────────────────────────────────────────────────────────
  // drill: the Notes editor's AX label (labelIn 'note' is the research's guess), read through `selection` before this is lifted
  {
    id: 'notes.new_note_titled', app: NOTES, status: 'held', opens: true,
    title: 'New note with a title',
    description: 'Types only the title, into the new note\'s proven editor; they keep typing the body. For asks like "new note called groceries" or "make a note named trip ideas".',
    params: {
      title: { kind: 'span', description: "The note's title, in their words" },
    },
    steps: [
      { op: 'activate' },
      { op: 'focus_mark' },
      { op: 'key', chord: 'cmd+n' },
      { op: 'focus_moved', timeoutMs: 800, labelIn: ['note'] },
      { op: 'type', param: 'title' },
    ],
    tier: 'reversible', cues: ['bana', 'new note', 'make a note', 'create a note', 'start a note', 'new note titled', 'create a note titled', 'naam ka note bana'],
    reply: 'Started a note called {title}.',
  },
  // drill: the Notes editor's AX label (labelIn 'note' is the research's guess), read through `selection` before this is lifted
  {
    id: 'notes.new_note_with_text', app: NOTES, status: 'held',
    title: 'Write their words into a new note',
    description: 'Their words into a new note\'s proven editor; nothing is pressed after them. For asks like "note down buy milk eggs and bread" or "take a note: call the bank Monday".',
    params: {
      text: { kind: 'literal', description: 'The exact words to put in the note', cut: ['after_saying', 'to_end'] },
    },
    steps: [
      { op: 'activate' },
      { op: 'focus_mark' },
      { op: 'key', chord: 'cmd+n' },
      { op: 'focus_moved', timeoutMs: 800, labelIn: ['note'] },
      { op: 'type', param: 'text' },
    ],
    tier: 'reversible', cues: ['jot', 'likh', 'write down', 'note down', 'take a note', 'save a note', 'new note', 'make a note', 'create a note', 'make a note that says', 'jot this down', 'ek note mein likh'],
    reply: 'Put it in a new note: {text}',
  },
  // ── Search ────────────────────────────────────────────────────────────────────────────────────
  // drill: the search field's AX label (labelIn), read through `selection` before this is lifted
  {
    id: 'notes.search', app: NOTES, status: 'held', opens: true,
    title: 'Search all notes',
    description: 'Notes filters as the words are typed; nothing is pressed after them. For asks like "search my notes for kyoto" or "find notes about the offsite".',
    params: {
      query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' },
    },
    steps: [
      { op: 'activate' },
      { op: 'focus_mark' },
      { op: 'key', chord: 'cmd+opt+f' },
      { op: 'focus_moved', timeoutMs: 800, labelIn: ['search'] },
      { op: 'type', param: 'query' },
    ],
    tier: 'reversible', cues: ['search', 'find', 'look', 'look through'],
    reply: 'Searching your notes for {query}.',
  },
  // The research marks it front-only: "this note" is the open one. Finding moves nothing, so it runs
  // on the open note when named too. drill: the find bar's AX label (labelIn) before this is lifted
  {
    id: 'notes.find_in_note', app: NOTES, status: 'held', opens: true,
    title: 'Find words in the open note',
    description: 'Only the open note. For asks like "find budget in this note" or "search this note for Friday".',
    params: {
      query: { kind: 'literal', description: 'The exact words to find in the open note', cut: 'query' },
    },
    steps: [
      { op: 'activate' },
      { op: 'focus_mark' },
      { op: 'key', chord: 'cmd+f' },
      { op: 'focus_moved', timeoutMs: 800, labelIn: ['find', 'search'] },
      { op: 'type', param: 'query' },
    ],
    tier: 'reversible', cues: ['find', 'search', 'where', 'dhoondo', 'search this note'],
    reply: 'Finding {query} in this note.',
  },
  opening(chord('notes.show_attachments', 'Show the attachments browser', 'May toggle back; the reply says what was pressed. For asks like "show all my attachments in Notes" or "open the attachments browser".', 'cmd+3', ['attachments', 'attachment', 'files', 'all my attachments', 'attachments browser', 'saare attachments dikhao', 'attachments view'], 'Showing your attachments in Notes.')),
  // ── R3c: the catalog rulings' accepts ─────────────────────────────────────────────────────────
  // View menu radio items: the note list as a list or a gallery.
  opening(menu('notes.list_view', 'Show notes as a list', 'For asks like "switch Notes to list view" or "show my notes as a list".', ['View', 'as List'], ['list', 'list view', 'as a list'], 'Showing your notes as a list.')),
  opening(menu('notes.gallery_view', 'Show notes as a gallery', 'For asks like "show my notes as a gallery" or "gallery view in Notes".', ['View', 'as Gallery'], ['gallery', 'thumbnails', 'gallery view', 'as a gallery', 'as thumbnails', 'gallery view mein dikhao'], 'Showing your notes as a gallery.')),
  // The folders sidebar. Its title flips, so each press is directional; "hide" names the hide.
  opening(menu('notes.show_sidebar', 'Show the Notes sidebar', 'The folders list. For asks like "show the folders in Notes" or "bring back the Notes sidebar".', ['View', 'Show Sidebar'], ['sidebar', 'folders', 'back the sidebar', 'back the notes sidebar', 'sidebar dikhao'], 'Showed the sidebar in Notes.', 'Hide Sidebar')),
  menu('notes.hide_sidebar', 'Hide the Notes sidebar', 'The folders list. For asks like "hide the sidebar in Notes" or "notes ka sidebar hatao".', ['View', 'Hide Sidebar'], ['hide', 'collapse', 'hatao', 'chhupao', 'hide the sidebar', 'hide sidebar', 'hide the folders', 'hide the folder list', 'collapse the sidebar', 'sidebar hatao'], 'Hid the sidebar in Notes.', 'Show Sidebar'),
  // View > Sort By: the order of the shown folder; nothing moves.
  menu('notes.sort_by_date_edited', 'Sort notes by date edited', 'For asks like "order notes by last edited" or "sort by date edited".', ['View', 'Sort By', 'Date Edited'], [...SORT, 'edited', 'date edited', 'by date edited', 'by last edited'], 'Sorted your notes by date edited.'),
  menu('notes.sort_by_date_created', 'Sort notes by date created', 'For asks like "sort notes by date created" or "sort my notes by creation date".', ['View', 'Sort By', 'Date Created'], [...SORT, 'created', 'date created', 'by date created', 'creation', 'by creation date'], 'Sorted your notes by date created.'),
  menu('notes.sort_by_title', 'Sort notes by title', 'For asks like "sort my notes by title" or "sort Notes alphabetically".', ['View', 'Sort By', 'Title'], [...SORT, 'title', 'alphabetically', 'alphabetical', 'by title'], 'Sorted your notes by title.'),
  // The note text's size only. "zoom" is also the Zoom app's name, so those asks are the model's.
  menu('notes.zoom_in', 'Make the note text bigger', 'For asks like "make the note text bigger" or "note ka text bada karo".', ['View', 'Zoom In'], ['bigger', 'larger', 'bada', 'text bigger', 'text larger', 'note text bigger', 'note ka text bada'], 'Made the note text bigger.'),
  menu('notes.zoom_out', 'Make the note text smaller', 'For asks like "make the text smaller in notes" or "notes ka text chhota karo".', ['View', 'Zoom Out'], ['smaller', 'chhota', 'text smaller', 'note text smaller', 'text chhota'], 'Made the note text smaller.'),
  menu('notes.actual_size', 'Put the note text back to actual size', 'For asks like "actual size in notes" or "back to normal size in notes".', ['View', 'Actual Size'], ['actual', 'normal', 'actual size', 'normal size', 'back to normal size', 'text normal size'], 'Put the note text back to actual size.'),
];
